import React, { forwardRef, useCallback, useState } from "react";
import Link from "next/link";
import { observer } from "mobx-react";
import Button from "ui/Button/Button";
import ProductPreviewImage from "ui/ProductPreviewImage/ProductPreviewImage";
import AddToFavorites from "common/AddToFavorites/AddToFavorites";
import Rating from "common/Rating/Rating";
import Price from "common/Price/Price";
import AddToCart from "common/AddToCart/AddToCart";
import DeliveryBlock from "common/ProductPreview/components/DeliveryBlock/DeliveryBlock";
import Badges from "common/Badges/Badges";
import { useStore } from "store";
import { useExperiment, useUrl } from "helpers/hooks";
import { formatDate } from "helpers/date";
import { cn } from "helpers/style";
import { withTrailSlash } from "helpers/url";
import { ROUTE_CART } from "constants/routes";
import { Props as PreviewProps, CommonProps } from "../../ProductPreview";
import Prices from "../Prices/Prices";
import BundleList from "../BundleList/BundleList";
import styles from "./Regular.module.scss";
import { RegularCardProduct } from "./types";

type Props = PreviewProps &
  CommonProps & {
    product: RegularCardProduct;
  };

const Regular = forwardRef<HTMLDivElement, Props>(
  (
    {
      showExpiringPrices,
      product,
      imageSrc,
      analyticsMeta,
      onClick,
      showReviewCount,
      ellipsis,
      onOpenNotify,
      hideCartToast,
      onInteract,
      disableLazyImage,
      pricePrefix,
      addCartText,
      price,
      isEmployeeMode,
      withSKU,
      prices,
      isNewPricePrefix,
    },
    ref
  ) => {
    const { cart } = useStore();
    const productInCart = cart.get(product.id, price);
    const isAvailable =
      ((product.availableForOrder || product.availableForBooking) &&
        Boolean(price)) ||
      (price?.isExpirating && Boolean(price?.price) && showExpiringPrices);
    //const hasAnalogs = false;
    const showRating = Boolean(
      (!showReviewCount && product.rating) ||
        (showReviewCount && product.rating && product.reviewsCount)
    );
    const { getEntityLinkProps } = useUrl();
    const exCountry = useExperiment("ex_country");
    const [currentPrice, setCurrentPrice] = useState(price);
    const showPrices = prices?.length > 1 && showExpiringPrices;
    const onClickHandler = useCallback(
      () => onClick && onClick({ product }),
      [onClick, product]
    );

    const classNamePriceCurrency =
      !isNewPricePrefix && styles.regularPriceContainerPriceCurrency;

    return (
      <div
        className={styles.regularContainer}
        data-id={product.id}
        data-diginetica="produc_special_diginetica"
      >
        <div className={styles.regularWrapper} ref={ref}>
          <div className={styles.regularUpper}>
            <ProductPreviewImage
              src={imageSrc}
              ariaLabel={product.name}
              onClick={onClickHandler}
              onMouseDown={onInteract}
              linkProps={getEntityLinkProps("product", product)}
              priority={disableLazyImage}
              title={product.name}
              alt={product.name}
            />

            {product.badges && product.badges.length > 0 && (
              <div className={styles.regularBadges}>
                <Badges
                  badges={product.badges}
                  tooltipProps={{
                    maxWidth: 292,
                    placement: "top-start",
                  }}
                  alt="Шильдик акции"
                />
              </div>
            )}

            <div className={styles.regularUpperActions}>
              <AddToFavorites product={product} />
            </div>
          </div>

          {showRating && (
            <div className={styles.regularRatingContainer}>
              <Rating
                value={product.rating}
                label={showReviewCount && product.reviewsCount}
              />
            </div>
          )}

          <Link
            {...getEntityLinkProps("product", product)}
            className={cn([
              styles.regularTitle,
              ellipsis && styles[`regularTitleEllipsis${ellipsis}`],
            ])}
            passHref
          >
            <span
              onClick={() => onClick && onClick({ product })}
              onMouseDown={onInteract}
              title={product.name}
            >
              {product.name}
            </span>
          </Link>

          {product.isBundle && (
            <BundleList
              className={styles.regularBundleList}
              items={product.bundleItemsSimple}
              link={getEntityLinkProps("product", product)}
            />
          )}

          {product.isAdv && product.warning && (
            <div className={styles.regularDisclaimer}>{product.warning}</div>
          )}

          {exCountry && product.producerCountry && (
            <div className={styles.regularCountryContainer}>
              {product.producerCountry}
            </div>
          )}

          {(!showPrices || isEmployeeMode) && currentPrice?.dateExpired && (
            <div className={styles.regularExpiration}>
              Годен до {formatDate(currentPrice?.dateExpired, "DD.MM.YYYY")}
            </div>
          )}

          {withSKU && product.sku && (
            <span className={styles.regularSku}>Артикул: {product.sku}</span>
          )}

          {isAvailable && (
            <div
              className={cn([
                styles.regularPriceContainer,
                isAvailable && styles.regularPriceContainerAvailable,
              ])}
            >
              {showPrices ? (
                <Prices
                  product={product}
                  prices={prices}
                  onPriceChange={setCurrentPrice}
                  activePrice={currentPrice}
                  pricePrefix={pricePrefix}
                  isAvailable={isAvailable}
                  showExpiryDate={
                    (isEmployeeMode && price.priceTypeID == 4) ||
                    !isEmployeeMode
                  }
                  classNamePrice={classNamePriceCurrency}
                  isNewPriceStyle={isNewPricePrefix}
                />
              ) : (
                <Price
                  hasOnlyBookingPrices={
                    product.hasOnlyBookingPrices || isEmployeeMode
                  }
                  hasPrefix={currentPrice.hasPrefix}
                  price={currentPrice.price}
                  priceOld={currentPrice.priceOld}
                  discount={currentPrice.discount}
                  prefix={pricePrefix}
                  classNamePrice={classNamePriceCurrency}
                  isNewPriceStyle={isNewPricePrefix}
                />
              )}
            </div>
          )}

          <div
            className={cn([
              styles.regularAddToCartContainer,
              !isAvailable && styles.regularAddToCartContainerNotAvailable,
            ])}
          >
            {isAvailable && (
              <AddToCart
                showExpiringPrices={showExpiringPrices}
                counterWide
                counterSize="large"
                product={product}
                price={currentPrice}
                analyticsMeta={analyticsMeta}
                hideCartToast={hideCartToast}
                text={addCartText}
                showOnlyButton={isEmployeeMode && !showExpiringPrices}
                className={styles.regularAddToCartContainerInner}
              />
            )}

            {!isAvailable && (
              <>
                {product.lastPrice !== 0 && (
                  <Price
                    price={product.lastPrice}
                    prefix={pricePrefix}
                    className={styles.lastPrice}
                    classNamePrice={classNamePriceCurrency}
                    isNewPriceStyle={isNewPricePrefix}
                  />
                )}

                <div className={styles.regularNotAvailableContainer}>
                  <div className={styles.regularNotAvailableTitle}>
                    Нет в наличии
                  </div>
                  <Button onClick={onOpenNotify} orange outlined fluid />
                </div>
              </>
            )}

            <div className={styles.regularAdditionalContainer}>
              {isAvailable && productInCart && (
                <a
                  href={withTrailSlash(ROUTE_CART)}
                  className={styles.regularAdditionalLink}
                >
                  Перейти в Корзину
                </a>
              )}

              {!productInCart && (
                <DeliveryBlock
                  className={styles.regularDeliveryBlock}
                  product={product}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default observer(Regular);
